import { createSlice } from '@reduxjs/toolkit';
import { defineState } from 'redux-localstore';

const initialState = defineState(
  {
    gameStarted: false,
    scoreA: 0,
    scoreB: 0,
    time: { minutes: 0, seconds: 0 },
    playersPerTeam: 6,
    pointsHistory: [],
    genderBalanceMode: false,
    genderRestrictedMode: false,
    femalesPerTeam: 1,
    setterRestrictedMode: false,
    showWinPercentage: false,
    autoBalanceMode: true,
    remoteControlEnabled: false,
    maxRallySeconds: 0,
    lastPointSeconds: null,
    timerPaused: false,
  },
)('game');

const elapsedSeconds = (time) => (time.minutes * 60) + time.seconds;
// Tempo médio de saque descontado de cada rally.
const SERVE_SECONDS = 10;

export const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    startGameCounter: (state) => {
      state.gameStarted = true;
      state.maxRallySeconds = 0;
      state.lastPointSeconds = null;
      state.timerPaused = false;
    },
    toggleTimerPaused: (state) => {
      state.timerPaused = !state.timerPaused;
    },
    resetTimer: (state) => {
      state.time = { minutes: 0, seconds: 0 };
      state.lastPointSeconds = null;
    },
    resetScore: (state) => {
      state.scoreA = 0;
      state.scoreB = 0;
      state.maxRallySeconds = 0;
      state.lastPointSeconds = null;
    },
    stopGameCounter: (state) => {
      state.gameStarted = false;
      state.scoreA = 0;
      state.scoreB = 0;
      state.time = { minutes: 0, seconds: 0 };
      state.maxRallySeconds = 0;
      state.lastPointSeconds = null;
      state.timerPaused = false;
    },
    // Registra o rally que acabou de terminar (tempo desde o último ponto) e guarda o maior.
    registerPointRally: (state) => {
      const now = elapsedSeconds(state.time);
      // Primeiro ponto do jogo não conta rally (abertura costuma ser mais longa).
      if (state.lastPointSeconds === null || state.lastPointSeconds === undefined) {
        state.lastPointSeconds = now;
        return;
      }
      // Desconta o tempo de saque entre os pontos.
      const rally = Math.max(0, (now - state.lastPointSeconds) - SERVE_SECONDS);
      if (rally > state.maxRallySeconds) state.maxRallySeconds = rally;
      state.lastPointSeconds = now;
    },
    addScoreToBoard: (state, action) => {
      if (action.payload === 'A') {
        state.scoreA += 1;
      } else {
        state.scoreB += 1;
      }
    },
    cancelScoreToBoard: (state, action) => {
      if (action.payload === 'A' && state.scoreA > 0) {
        state.scoreA -= 1;
      } else if (action.payload === 'B' && state.scoreB > 0) {
        state.scoreB -= 1;
      }
    },
    setTime: (state, action) => {
      state.time = action.payload;
    },
    changePlayersPerTeam: (state, action) => {
      state.playersPerTeam = action.payload;
    },
    setPointsHistory: (state, action) => {
      state.pointsHistory = action.payload;
    },
    toggleGenderBalanceMode: (state) => {
      state.genderBalanceMode = !state.genderBalanceMode;
    },
    toggleGenderRestrictedMode: (state) => {
      state.genderRestrictedMode = !state.genderRestrictedMode;
    },
    setFemalesPerTeam: (state, action) => {
      state.femalesPerTeam = action.payload;
    },
    toggleSetterRestrictedMode: (state) => {
      state.setterRestrictedMode = !state.setterRestrictedMode;
    },
    toggleShowWinPercentage: (state) => {
      state.showWinPercentage = !state.showWinPercentage;
    },
    toggleAutoBalanceMode: (state) => {
      state.autoBalanceMode = !state.autoBalanceMode;
    },
    toggleRemoteControl: (state) => {
      state.remoteControlEnabled = !state.remoteControlEnabled;
    },
  },
});

export const {
  startGameCounter,
  stopGameCounter,
  toggleTimerPaused,
  resetTimer,
  addScoreToBoard,
  cancelScoreToBoard,
  registerPointRally,
  setTime,
  resetScore,
  changePlayersPerTeam,
  setPointsHistory,
  toggleGenderBalanceMode,
  toggleGenderRestrictedMode,
  setFemalesPerTeam,
  toggleSetterRestrictedMode,
  toggleShowWinPercentage,
  toggleAutoBalanceMode,
  toggleRemoteControl,
} = gameSlice.actions;
